<template>
  <div class="inline-screening-container">
    <div class="screening-header">
      <h3>{{ title }} Screening Questions</h3>
      <p class="screening-description">Please answer the following screening questions to continue:</p>
    </div>

    <vue-final-modal v-model="showInfoModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showInfoModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <p>{{ information }}</p>
      </div>
    </vue-final-modal>

    <vue-final-modal v-model="showImageModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showImageModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <img :src="imageBase64" />
      </div>
    </vue-final-modal>

    <div class="question-groups-container">
      <div class="question-group" v-for="(questionsInGroup, groupName) in groupedVisibleQuestions" :key="groupName">
        <h4 class="group-title">{{ groupName }}</h4>
        <div class="screening-questions__list">
          <div class="screening-question" v-for="question of questionsInGroup" :key="question.id">
            <div class="question-header">
              <span>{{ question.title }}</span>

              <div class="question-actions">
                <button class="btn-icon" v-if="question.imageBase64" @click="showImage(question.imageBase64)"
                  title="View Image">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2.5L5.5 12 9 8.5V11h6V8.5l3.5 3.5-3.5 3.5z" />
                  </svg>
                </button>
                <button class="btn-icon" v-if="question.information" @click="showInformation(question)"
                  title="More Information">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="question-content">
              <p class="question-text">{{ question.question }}</p>

              <button class="response-button" @click="toggleResponse(question)"
                :class="{ active: question.response === true }">
                <span>{{ question.response === true ? 'YES' : 'NO' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="screening-actions">
      <button class="btn-secondary" @click="cancelScreening">
        <svg class="btn-icon" width="18" height="18" viewBox="0 0 24 24">
          <path fill="currentColor"
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
        Cancel
      </button>
      <button class="btn-primary" @click="confirmScreening">
        <svg class="btn-icon" width="18" height="18" viewBox="0 0 24 24">
          <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
        Confirm Screening
      </button>
    </div>
  </div>
</template>

<script>
import axios from '../../../../services/axios-service';

export default {
  name: 'InlineScreeningQuestions',
  props: {
    systemType: { type: Number, required: true },
    title: { type: String, required: true },
    preselectedScreeningIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      allQuestions: [],
      information: '',
      imageBase64: '',
      showInfoModal: false,
      showImageModal: false
    };
  },
  computed: {
    visibleQuestions() {
      const root = this.allQuestions.filter((q) => !q.relatedQuestionId);
      const conditional = this.allQuestions.filter((q) => {
        if (!q.relatedQuestionId) return false;
        const parent = this.allQuestions.find((p) => p.id === q.relatedQuestionId);
        if (!parent) return false;
        if (q.relatedQuestionShowResponse === null) return parent.response !== null && parent.response !== undefined;
        if (q.relatedQuestionShowResponse === 1) return parent.response === true;
        if (q.relatedQuestionShowResponse === 0) return parent.response !== true;
        return false;
      });
      return [...root, ...conditional].sort((a, b) => a.position - b.position);
    },
    groupedVisibleQuestions() {
      if (!this.visibleQuestions.length) return {};
      const groupOrder = [...new Set(this.allQuestions.map((q) => (q.group ? q.group.trim() : 'Uncategorized')))].filter(Boolean);
      const grouped = this.visibleQuestions.reduce((acc, q) => {
        const g = q.group ? q.group.trim() : 'Uncategorized';
        if (!acc[g]) acc[g] = [];
        acc[g].push(q);
        return acc;
      }, {});
      const ordered = {};
      groupOrder.forEach((g) => {
        if (grouped[g]) ordered[g] = grouped[g];
      });
      return ordered;
    },
  },
  mounted() {
    this.loadScreeningQuestions();
  },
  methods: {
    async loadScreeningQuestions() {
      this.isLoading = true;
      try {
        const existingScreeningQuestions = JSON.parse(localStorage.getItem("@screening-questions"));

        const response = await axios.get(`/api/screeningquestion?systemType=${this.systemType}`);
        this.allQuestions = response.data.map((q) => ({ ...q, response: false }));
        const list = response.data.map((q) => ({
          ...q,
          response: typeof q.response === 'boolean' ? q.response : false,
          autoSelected: false,
        }));

        const preset = new Set(this.preselectedScreeningIds || []);
        for (const q of list) {
          const preselectedFromScreening = existingScreeningQuestions.filter(e => e.pairedQuestionId == q.id && e.response == true).length > 0;
      
          if (preset.has(q.id) || preselectedFromScreening) {
            q.response = true;
            q.autoSelected = true;
          }
        }

        this.allQuestions = list;
      } catch (e) {
        console.error('Error loading screening questions:', e);
        this.$emit('error', 'Failed to load screening questions');
      } finally {
        this.isLoading = false;
      }
    },
    toggleResponse(question) {
      question.response = !question.response;
      const idx = this.allQuestions.findIndex((q) => q.id === question.id);
      if (idx !== -1) this.allQuestions[idx].response = question.response;
      this.$forceUpdate();
    },
    showInformation(q) {
      this.information = q.information;
      this.showInfoModal = true;
    },
    showImage(img) {
      this.imageBase64 = img;
      this.showImageModal = true;
    },
    confirmScreening() {
      const selected = this.allQuestions.filter((q) => q.response === true).map((q) => q.id);
      const deselected = this.allQuestions.filter((q) => q.response === false).map((q) => q.id);
      this.$emit('screening-confirmed', {
        selectedServiceQuestionIds: selected,
        deselectedServiceQuestionIds: deselected,
        systemType: this.systemType,
      });
    },
    cancelScreening() {
      this.$emit('screening-cancelled');
    },
  },
  watch: {
    systemType: {
      immediate: false,
      handler() {
        this.loadScreeningQuestions();
      }
    },
    preselectedScreeningIds(newVal) {
      if (!Array.isArray(newVal) || !this.allQuestions.length) return;
      const preset = new Set(newVal);
      for (const q of this.allQuestions) {
        if (preset.has(q.id)) {
          q.response = true;
          q.autoSelected = true;
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../breakpoints.scss";

.inline-screening-container {
  background: #F8FAFC;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  padding: 24px;
  margin: 20px 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.screening-header {
  margin-bottom: 24px;
  text-align: center;

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #1E293B;
    margin: 0 0 8px 0;
  }

  .screening-description {
    color: #64748B;
    margin: 0;
    font-size: 14px;
  }
}

.question-groups-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
}

.group-title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #D1D5DB;
}

.screening-questions__list {
  display: grid;
  gap: 12px;

  @include md {
    grid-template-columns: 1fr 1fr;
  }
}

.screening-question {
  background: white;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
    transform: translateY(-1px);
  }
}

.question-header {
  background: linear-gradient(135deg, #64748B, #475569);
  color: white;
  padding: 10px 12px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  span {
    flex: 1;
    text-align: left;
  }

  .question-actions {
    display: flex;
    gap: 6px;
  }

  .btn-icon {
    background: transparent;
    border: none;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.2s;
    padding: 0;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
}

.question-content {
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .question-text {
    flex: 1;
    margin: 0;
    color: #000001;
    font-size: 14px;
    line-height: 1.4;
  }

  .response-button {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: #F3F4F6;
    border: 2px solid #D1D5DB;
    color: #000001;
    font-weight: 600;
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      background: #E5E7EB;
      border-color: #9CA3AF;
    }

    &.active {
      background: #64748B;
      border-color: #64748B;
      color: white;
      box-shadow: 0 2px 4px rgba(100, 116, 139, 0.2);
    }
  }
}

.screening-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  border-top: 1px solid #E5E7EB;
  padding-top: 20px;

  @include md {
    justify-content: flex-end;
  }
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #10B981, #059669);
  color: white;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}

// Modal Styles
::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 24px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: white;
  width: 90%;
  max-width: 600px;
  overflow-y: auto;
  max-height: 80vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 4px;

    @include md {
      width: auto;
      max-width: 100%;
      max-height: 60vh;
    }
  }

  p {
    line-height: 1.6;
    color: #000001;
    font-size: 16px;
  }
}

.modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 32px;
  color: #6B7280;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }
}
</style>